import { PlacesType, PositionStrategy, Tooltip } from 'react-tooltip';
import { styled } from 'styled-components';
import { FormHelpBlock } from '@motili/ui';
import CheckCircleOutlineOutlined from '@mui/icons-material/CheckCircleOutlineOutlined';
import CancelOutlined from '@mui/icons-material/CancelOutlined';
import { ValidationStatus } from 'common/types/validationStates';

export type ValidationState =
    | ValidationStatus.ERROR
    | ValidationStatus.WARNING
    | ValidationStatus.SUCCESS
    | null
    | undefined;

export interface Validation {
    state?: ValidationState;
    message?: string | null;
    toolTip?: {
        id: string;
        place: PlacesType;
        zIndex?: number;
        positionStrategy?: PositionStrategy;
    };
    hideValidationMessage?: boolean;
}

interface FormValidationDisplayProps {
    validation: Validation | undefined;
    style?: object;
    messageStyle?: object;
    hideIcon?: boolean;
}

const FormValidationDisplay = ({
    validation,
    style,
    messageStyle,
    hideIcon,
}: FormValidationDisplayProps) => {
    const getValidationStateIcon = (validationState: ValidationState) => {
        if (hideIcon) return undefined;
        switch (validationState) {
            case ValidationStatus.SUCCESS:
                return <SuccessIcon />;
            case ValidationStatus.ERROR:
                return <ErrorIcon />;
            default:
                return undefined;
        }
    };
    const getValidationMessage = (
        validationState: ValidationState,
        validationMessage: Validation['message']
    ) => {
        switch (validationState) {
            case ValidationStatus.ERROR:
                return validationMessage || 'Invalid';
            case ValidationStatus.WARNING:
                return validationMessage || 'Warning';
            default:
                return '';
        }
    };
    const validationMessage = getValidationMessage(
        validation?.state,
        validation?.message
    );

    const renderValidationMessage = () => {
        if (!validationMessage) return undefined;
        if (validation?.hideValidationMessage) {
            return undefined;
        }
        if (validation?.toolTip) {
            return (
                <StyledTooltip
                    id={validation?.toolTip.id}
                    variant={validation?.state || 'info'}
                    place={validation.toolTip.place}
                    disableStyleInjection='core'
                    positionStrategy={validation.toolTip.positionStrategy}
                >
                    {validationMessage}
                </StyledTooltip>
            );
        }
        return (
            <FormHelpBlock style={messageStyle}>
                {validationMessage}
            </FormHelpBlock>
        );
    };

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', ...style }}>
                {getValidationStateIcon(validation?.state)}
            </div>
            {renderValidationMessage()}
        </>
    );
};
const StyledTooltip = styled(Tooltip)<{ zIndex?: number }>`
    z-index: ${props => props.zIndex || 3};
    visibility: visible;
`;

const SuccessIcon = styled(CheckCircleOutlineOutlined)`
    font-size: 20px !important;
`;
const ErrorIcon = styled(CancelOutlined)`
    font-size: 20px !important;
`;

export default FormValidationDisplay;
